import React, { useState } from 'react';
import PropTypes from 'prop-types';
import path from 'path-browserify';
import { get, remove, map } from 'lodash';
import { Spinner, SpinnerSize, TooltipHost } from 'office-ui-fabric-react';
import { IconButton, PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import Module from '../Module';
import ModuleContent from '../ModuleContent';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import DetailsList from '../DetailsList';
import TextButton from '../TextButton';
import ConfirmationDialog from '../ConfirmationDialog';
import DetailsListOverflowTooltip from '../DetailsListOverflowTooltip';
import { formatDateString, pxToRem } from '../../utils';
import styles from './PendingAttachmentApprovals.style';

const rejectConfirmation = {
  type: 'reject',
  content: 'Are you sure you want to reject the attachments for partner visibility?',
  button: 'Reject',
};

const approveConfirmation = {
  type: 'approve',
  content: 'Are you sure you want to share the attachments for partner visibility?',
  button: 'Approve',
};

const PendingAttachmentApprovals = ({
  pendingAttachments,
  onDownload,
  onRemove,
  onApproveClick,
  onRejectClick,
  onApproveAllSelected,
  onRejectAllSelected,
  isLoading,
  ...other
}) => {
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [selectAllAttachments, setSelectAllAttachments] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRejectClick, setIsRejectClick] = useState(false);

  const onHandleColumnClick = () => {
    if (selectAllAttachments) {
      setSelectedAttachments([]);
      setSelectAllAttachments(false);
    } else {
      const items = map(pendingAttachments, 'attachmentId');

      setSelectedAttachments(items);
      setSelectAllAttachments(true);
    }
  };

  const onSelect = itemId => {
    const updated = [...selectedAttachments];
    if (updated.includes(itemId)) {
      remove(updated, id => id === itemId);
    } else {
      updated.push(itemId);
    }
    setSelectAllAttachments(updated?.length === pendingAttachments?.length);
    setSelectedAttachments(updated);
  };

  const handleRejectAllSelected = () => {
    setDialogContent(rejectConfirmation);
    setIsRejectClick(true);
    setIsDialogVisible(true);
  };

  const handleApproveAllSelected = () => {
    setDialogContent(approveConfirmation);
    setIsDialogVisible(true);
  };

  const handleDialogSubmit = async (formValue = {}) => {
    if (dialogContent.type === 'approve') {
      await onApproveAllSelected(selectedAttachments);
    } else if (dialogContent.type === 'reject') {
      await onRejectAllSelected(selectedAttachments, formValue);
    }
    setSelectedAttachments([]);
    setSelectAllAttachments(false);
    setIsRejectClick(false);
    setIsDialogVisible(false);
  };

  const handleDownloadAllSelected = async () => {
    setIsDownloading(true);
    await onDownload(selectedAttachments);
    setIsDownloading(false);
  };

  const handleRejectOrApprove = (itemId, actionType) => {
    const isSelected = selectedAttachments.includes(itemId);
    if (isSelected) {
      const itemsSelected = selectedAttachments.filter(id => id !== itemId);
      setSelectedAttachments(itemsSelected);
      setSelectAllAttachments(false);
    }
    if (actionType === 'approve') {
      onApproveClick(itemId);
    } else if (actionType === 'reject') {
      onRejectClick(itemId);
    }
  };

  const selectMenuProps = {
    items: [
      {
        key: 'selectPendingApproval',
        text: 'Select To Approve/Reject/Download',
        iconProps: { iconName: 'LocationCircle' },
      },
    ],
  };

  const columns = {
    selectIcon: {
      key: 'select',
      name: 'select',
      headerClassName: styles.iconColumnHeaderSelectButton,
      arianame: 'icon',
      iconName: selectAllAttachments ? 'SkypeCircleCheck' : 'LocationCircle',
      isIconOnly: true,
      fieldName: 'select',
      minWidth: 16,
      maxWidth: 16,
      onColumnClick: onHandleColumnClick,
      onRender: item => {
        const attachmentId = get(item, 'attachmentId');
        const iconName = selectedAttachments.includes(attachmentId)
          ? 'SkypeCircleCheck'
          : 'LocationCircle';
        return (
          <div>
            <IconButton
              className={styles.selectButton}
              menuProps={selectMenuProps}
              ariaLabel="Select"
              iconProps={{ iconName }}
              menuIconProps={{ iconName: '' }}
              onMenuClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onSelect(attachmentId);
              }}
            />
          </div>
        );
      },
    },
    fileIcon: {
      key: 'fileIcon',
      name: 'File Type',
      isIconOnly: true,
      isHeaderVisible: false,
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      columnActionsMode: 0,
      minWidth: 16,
      maxWidth: 16,
      onRender(attachment) {
        const extension = path.extname(attachment.displayName);
        return (
          <FontIcon {...getFileTypeIconProps({ extension, size: 16, imageFileType: 'png' })} />
        );
      },
    },
    commentIcon: {
      key: 'comment',
      name: 'Comment',
      fieldName: 'displayName',
      minWidth: 30,
      maxWidth: 30,
      columnActionsMode: 0,
      isPadded: true,
      className: styles.alignCenter,
      onRender(attachment) {
        if (attachment.comment) {
          return (
            <TooltipHost
              content={attachment.comment}
              calloutProps={{
                gapSpace: 0,
                target: `#select-comment-${attachment.attachmentId}`,
              }}
              styles={{
                root: {
                  display: 'flex',
                },
              }}
            >
              <IconButton
                iconProps={{ iconName: 'CannedChat', style: { fontSize: pxToRem(14) } }}
                ariaLabel="Comment"
                id={`select-comment-${attachment.attachmentId}`}
                className={styles.infoIcon}
              />
            </TooltipHost>
          );
        }
        return '';
      },
    },
    fileName: {
      key: 'name',
      name: 'File Name',
      fieldName: 'displayName',
      minWidth: 120,
      maxWidth: 120,
      columnActionsMode: 0,
      isPadded: true,
      onRender: item => (
        <DetailsListOverflowTooltip
          ariaLabel="File name"
          displayContent={item.displayName}
          tooltipContent={item.displayName}
        />
      ),
    },
    requestor: {
      key: 'requestor',
      name: 'Requestor',
      fieldName: 'requestor',
      columnActionsMode: 0,
      minWidth: 120,
      maxWidth: 120,
      isPadded: true,
      onRender: item => (
        <DetailsListOverflowTooltip
          ariaLabel="requestor"
          displayContent={item.requestor?.displayName || item.requestor}
          tooltipContent={item.requestor?.displayName || item.requestor}
        />
      ),
    },
    lastModified: {
      key: 'lastUpdatedTimestamp',
      name: 'Last Modified',
      fieldName: 'lastUpdatedTimestamp',
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      isPadded: true,
      onRender: item => (
        <DetailsListOverflowTooltip
          ariaLabel="last modified"
          displayContent={formatDateString(item.lastUpdatedTimestamp)}
        />
      ),
    },
    owner: {
      key: 'createdBy',
      name: 'Owner',
      fieldName: 'owner',
      columnActionsMode: 0,
      minWidth: 120,
      maxWidth: 120,
      isPadded: true,
      onRender: item => (
        <div style={{ width: '100%' }}>
          <DetailsListOverflowTooltip
            ariaLabel="owner"
            displayContent={item.createdBy?.displayName || item.createdBy?.oid}
          />
          <br /> {formatDateString(item.createdTimestamp)}
        </div>
      ),
    },
    remove: {
      key: 'remove',
      name: 'Remove',
      arianame: 'remove',
      isIconOnly: true,
      fieldName: 'remove',
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onRemove(item.attachmentId);
            }}
          >
            Remove
          </TextButton>
        );
      },
    },
    download: {
      key: 'download',
      name: 'Download',
      arianame: 'download',
      isIconOnly: true,
      columnActionsMode: 0,
      fieldName: 'download',
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onDownload(item);
            }}
          >
            Download
          </TextButton>
        );
      },
    },
    reject: {
      key: 'reject',
      name: 'Reject',
      arianame: 'reject',
      isIconOnly: true,
      fieldName: 'reject',
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              handleRejectOrApprove(item.attachmentId, 'reject');
            }}
          >
            Reject
          </TextButton>
        );
      },
    },
    approve: {
      key: 'approve',
      name: 'Approve',
      arianame: 'approve',
      isIconOnly: true,
      fieldName: 'approve',
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              handleRejectOrApprove(item.attachmentId, 'approve');
            }}
          >
            Approve
          </TextButton>
        );
      },
    },
  };

  const approverColumns = [
    columns.selectIcon,
    columns.fileIcon,
    columns.fileName,
    columns.requestor,
    columns.lastModified,
    columns.owner,
    columns.commentIcon,
    columns.download,
    columns.reject,
    columns.approve,
  ];

  return (
    <Module>
      <ModuleContent>
        <SubModule closed>
          <SubModuleHeader id="pendingApproval" aria-label="Pending Approval">
            Pending Approval
          </SubModuleHeader>
          <SubModuleContent>
            {pendingAttachments && pendingAttachments.length > 0 && (
              <DetailsList
                columns={approverColumns}
                items={pendingAttachments}
                multiLine
                {...other}
              />
            )}
            <div className={styles.buttonContainer} data-testid="button-container">
              <PrimaryButton
                onClick={handleDownloadAllSelected}
                disabled={!selectedAttachments?.length}
                className={styles.actionButton}
                data-testid="download-all-button"
              >
                Download all Selected
                {isDownloading && (
                  <Spinner className={styles.loadingSpinner} size={SpinnerSize.small} />
                )}
              </PrimaryButton>
              <DefaultButton
                onClick={handleRejectAllSelected}
                disabled={!selectedAttachments?.length}
                className={styles.actionButton}
                data-testid="reject-all-button"
              >
                Reject all Selected
              </DefaultButton>
              <PrimaryButton
                type="submit"
                disabled={!selectedAttachments?.length}
                onClick={handleApproveAllSelected}
                data-testid="approval-all-button"
              >
                Approve all Selected
              </PrimaryButton>
            </div>
          </SubModuleContent>
        </SubModule>
      </ModuleContent>
      <ConfirmationDialog
        onDismiss={() => {
          setIsDialogVisible(false);
          setIsRejectClick(false);
        }}
        onSubmit={handleDialogSubmit}
        isLoading={isLoading}
        hidden={!isDialogVisible}
        isRejectClick={isRejectClick}
        dialogContentProps={{
          type: DialogType.close,
          title: 'Confirmation',
          closeButtonAriaLabel: 'Close',
        }}
        dialogBodyContent={{
          content: dialogContent?.content,
          buttonText: dialogContent?.button,
        }}
      />
    </Module>
  );
};

PendingAttachmentApprovals.propTypes = {
  isLoading: PropTypes.bool,
  pendingAttachments: PropTypes.array,
  onDownload: PropTypes.func,
  onRemove: PropTypes.func,
  onApproveClick: PropTypes.func,
  onRejectClick: PropTypes.func,
  onApproveAllSelected: PropTypes.func,
  onRejectAllSelected: PropTypes.func,
};

PendingAttachmentApprovals.defaultProps = {
  isLoading: false,
  pendingAttachments: [],
  onDownload: () => {},
  onRemove: () => {},
  onApproveClick: () => {},
  onRejectClick: () => {},
  onApproveAllSelected: () => {},
  onRejectAllSelected: () => {},
};

export default PendingAttachmentApprovals;
